import { useEffect, useRef } from 'react'

import { includes } from 'lodash-es'

import { usePrevious } from '../../common/hooks/usePrevious'
import { MULTIPLE_PRIMARY_NEEDS_ORIGINS } from '../searchForCareExperimentTypes'
import {
  sendMultiplePrimaryNeedsExperimentData,
  SendMultiplePrimaryNeedsExperimentDataParams,
} from '../sendMultiplePrimaryNeedsExperimentData'

/**
 * Hook that sends experiment data for the Multiple Primary Needs experiment when viewing homebase.
 *
 * This hook fires when the user views the homebase page and the searchForCareHeaderExperimentQ12025v2
 * Launch Darkly flag is enabled. The experiment data should only be sent once per homebase view
 * and only for users who are in the experiment.
 */

type UseSendMultiplePrimaryNeedsExperimentDataForHomebase = Omit<
  SendMultiplePrimaryNeedsExperimentDataParams,
  'origin'
> & {
  isHeaderExperimentEnabled?: boolean
  route: string
}

const hombaseRoutes = [
  '/secure/index/search-care', // web
  'Homebase', // native
]

export const useSendMultiplePrimaryNeedsExperimentDataForHomebase = ({
  canSelectMultiplePrimaryNeeds,
  userId,
  searchId,
  deviceUUID,
  customerName,
  postExperimentEntrypoint,
  ldClient,
  isLyraSelect,
  isHeaderExperimentEnabled,
  route,
}: UseSendMultiplePrimaryNeedsExperimentDataForHomebase) => {
  const hasTracked = useRef(false)
  const previousRoute = usePrevious(route)

  useEffect(() => {
    // Reset tracking when route changes
    if (route !== previousRoute) {
      hasTracked.current = false
    }
    if (includes(hombaseRoutes, route) && isHeaderExperimentEnabled && !hasTracked.current && userId) {
      sendMultiplePrimaryNeedsExperimentData({
        canSelectMultiplePrimaryNeeds,
        userId,
        searchId,
        deviceUUID,
        customerName,
        postExperimentEntrypoint,
        ldClient,
        isLyraSelect,
        origin: MULTIPLE_PRIMARY_NEEDS_ORIGINS.HOMEBASE,
      })
      hasTracked.current = true
    }
  }, [
    canSelectMultiplePrimaryNeeds,
    userId,
    searchId,
    deviceUUID,
    customerName,
    postExperimentEntrypoint,
    ldClient,
    isLyraSelect,
    route,
    isHeaderExperimentEnabled,
    previousRoute,
    hasTracked,
  ])
}
